import { useState, useEffect, useId } from 'react'
import type { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Megaphone, ChevronDown } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/ui/components/common/Card'
import { Button } from '@/ui/components/common/Button'
import { Label } from '@/ui/components/common/Label'
import { Input } from '@/ui/components/forms/Input'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { InvestigationSelector } from '@/ui/components/forms/InvestigationSelector'
import type { InvestigationSelection } from '@/ui/components/forms/InvestigationSelector'
import { useGetBreakingNews, useUpdateBreakingNews } from '@/application/services/settings.service'
import { useGetInvestigationById } from '@/application/services/investigation.service'

type BreakingLinkMode = 'none' | 'investigation' | 'link'

interface BreakingFormState {
  active: boolean
  linkMode: BreakingLinkMode
  investigation: InvestigationSelection
  link: string
  message: string
}

const EMPTY_FORM: BreakingFormState = {
  active: false,
  linkMode: 'none',
  investigation: { id: null, name: '' },
  link: '',
  message: '',
}

/** Editorial breaking-news banner shown on the news homepage layout. */
export function BreakingNewsSection() {
  const { t, i18n } = useTranslation()
  const language = i18n.language.substring(0, 2).toLowerCase()
  const { data: breaking, isLoading } = useGetBreakingNews(language)
  const { mutateAsync: updateBreaking, isPending } = useUpdateBreakingNews(language)
  const { data: linkedInvestigation } = useGetInvestigationById(breaking?.investigationId ?? '')

  const [form, setForm] = useState<BreakingFormState>(EMPTY_FORM)
  const [open, setOpen] = useState(false)
  const contentId = useId()

  useEffect(() => {
    if (breaking) {
      setForm({
        active: breaking.active,
        linkMode: breaking.investigationId ? 'investigation' : breaking.link ? 'link' : 'none',
        investigation: { id: breaking.investigationId, name: linkedInvestigation?.name ?? '' },
        link: breaking.link ?? '',
        message: breaking.message,
      })
    }
  }, [breaking, linkedInvestigation?.name])

  function handleMessageChange(event: ChangeEvent<HTMLInputElement>) {
    setForm((prev) => ({ ...prev, message: event.target.value }))
  }

  function handleInvestigationChange(investigation: InvestigationSelection) {
    setForm((prev) => ({ ...prev, investigation }))
  }

  function handleLinkChange(event: ChangeEvent<HTMLInputElement>) {
    setForm((prev) => ({ ...prev, link: event.target.value }))
  }

  // `investigation` and `link` are mutually exclusive — switching modes
  // clears whichever field the new mode doesn't use.
  function handleLinkModeChange(linkMode: BreakingLinkMode) {
    setForm((prev) => ({
      ...prev,
      linkMode,
      investigation: linkMode === 'investigation' ? prev.investigation : { id: null, name: '' },
      link: linkMode === 'link' ? prev.link : '',
    }))
  }

  function handleActiveToggle() {
    setForm((prev) => ({ ...prev, active: !prev.active }))
  }

  async function handleSave() {
    try {
      await updateBreaking({
        active: form.active,
        investigationId: form.linkMode === 'investigation' ? form.investigation.id : null,
        link: form.linkMode === 'link' ? form.link.trim() || null : null,
        message: form.message,
      })
      toast.success(t('admin.settings_breaking_saved'))
    } catch {
      toast.error(t('common.error'))
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="p-0">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          aria-controls={contentId}
          className="flex w-full items-start justify-between gap-2 p-6 text-left hover:bg-muted/50 transition-colors rounded-t-lg"
        >
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2">
              <Megaphone className="w-5 h-5" />
              {t('admin.settings_breaking_title')}
            </CardTitle>
            <CardDescription>{t('admin.settings_breaking_card_desc')}</CardDescription>
          </div>
          <ChevronDown
            aria-hidden="true"
            className={`w-5 h-5 shrink-0 text-muted-foreground transition-transform mt-1 ${
              open ? 'rotate-180' : ''
            }`}
          />
        </button>
      </CardHeader>
      {open && (
        <CardContent id={contentId} className="space-y-4">
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={handleActiveToggle}
              className="h-4 w-4 rounded border-border"
            />
            {t('admin.settings_breaking_active_label')}
          </label>

          <div className="space-y-2">
            <Label htmlFor="breaking-message">{t('admin.settings_breaking_message_label')}</Label>
            <Input id="breaking-message" value={form.message} onChange={handleMessageChange} />
          </div>

          <div className="space-y-2">
            <Label>{t('admin.settings_breaking_link_mode_label')}</Label>
            <div className="flex flex-wrap gap-4">
              {(['none', 'investigation', 'link'] as const).map((mode) => (
                <label key={mode} className="flex items-center gap-2 cursor-pointer text-sm">
                  <input
                    type="radio"
                    name="breaking-link-mode"
                    checked={form.linkMode === mode}
                    onChange={() => handleLinkModeChange(mode)}
                    className="h-4 w-4 border-border"
                  />
                  {mode === 'none'
                    ? t('admin.settings_breaking_link_mode_none')
                    : mode === 'investigation'
                      ? t('admin.settings_breaking_link_mode_investigation')
                      : t('admin.settings_breaking_link_mode_link')}
                </label>
              ))}
            </div>
          </div>

          {form.linkMode === 'investigation' && (
            <div className="space-y-2">
              <Label>{t('admin.settings_breaking_investigation_label')}</Label>
              <InvestigationSelector
                value={form.investigation}
                onChange={handleInvestigationChange}
              />
            </div>
          )}

          {form.linkMode === 'link' && (
            <div className="space-y-2">
              <Label htmlFor="breaking-link">{t('admin.settings_breaking_link_label')}</Label>
              <Input
                id="breaking-link"
                type="url"
                value={form.link}
                onChange={handleLinkChange}
                placeholder="https://…"
              />
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={() => void handleSave()} isLoading={isPending}>
              {t('admin.settings_breaking_save_button')}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  )
}
